import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { CountryCode } from '@capsico/contracts';
import logo from '../components/capsico-logo.png';
import { useI18n } from '../i18n';
import { useSignIn } from '../hooks/useSignIn';
import { useSession } from '../hooks/useSession';
import { useProfileQuery } from '../hooks/useProfile';
import { Button, DraftBar, Notice } from '../components/ui';
import { TurnstileBox } from '../components/TurnstileBox';

const COUNTRIES: { key: CountryCode; label: string }[] = [
  { key: 'IN', label: 'India +91' },
  { key: 'NP', label: 'Nepal +977' },
];

const inputClass =
  'h-12 w-full rounded-xl bg-surface px-4 text-base text-ink tabular shadow-[inset_0_0_0_1px_var(--color-field)] placeholder:text-ink-3 focus:shadow-[inset_0_0_0_1.5px_var(--color-brand-600)] focus:outline-none';

/** Pattern AG sign-in (C8): yellow hero with the logo, phone → 6-digit code, "Just browsing". */
export function SignInPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') ?? '/';
  const session = useSession();
  const profile = useProfileQuery(session.signedIn);
  const s = useSignIn();

  useEffect(() => {
    if (!session.signedIn || !profile.data) return;
    navigate(
      profile.data.complete ? next : `/account/profile?first=1&next=${encodeURIComponent(next)}`,
      { replace: true },
    );
  }, [session.signedIn, profile.data, navigate, next]);

  return (
    <div className="flex min-h-dvh flex-col bg-page">
      <div className="rounded-b-3xl bg-brand px-6 pt-[max(env(safe-area-inset-top),40px)] pb-14">
        <img src={logo} alt="Capsico" className="size-14 rounded-2xl" />
        <h1 className="mt-5 text-2xl font-bold text-on-brand">{t('signin.title')}</h1>
        <p className="mt-1 text-sm text-on-brand">{t('signin.subtitle')}</p>
      </div>

      <div className="-mt-8 px-4">
        <div className="space-y-4 rounded-sheet bg-surface p-5 shadow-[inset_0_0_0_1px_var(--color-hair)]">
          {s.step === 'phone' ? (
            <>
              {s.draftRestored && (
                <DraftBar
                  text={t('common.draftRestored')}
                  keepLabel={t('common.keep')}
                  discardLabel={t('common.discard')}
                  onKeep={s.keepDraft}
                  onDiscard={s.discardDraft}
                />
              )}
              <p className="text-sm font-semibold text-ink">{t('signin.phone')}</p>
              <div role="radiogroup" aria-label="Country" className="flex gap-2">
                {COUNTRIES.map((country) => (
                  <button
                    key={country.key}
                    type="button"
                    role="radio"
                    aria-checked={s.form.country === country.key}
                    onClick={() => s.setCountry(country.key)}
                    className={`h-8 rounded-full px-4 text-xs font-medium ${
                      s.form.country === country.key
                        ? 'bg-dark text-white'
                        : 'text-ink shadow-[inset_0_0_0_1px_var(--color-control)]'
                    }`}
                  >
                    {country.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <span className="inline-flex h-12 items-center rounded-xl bg-tile px-3 text-sm text-ink-2 tabular">
                  {s.dial}
                </span>
                <input
                  aria-label={t('signin.phone')}
                  inputMode="numeric"
                  autoComplete="tel-national"
                  value={s.form.number}
                  placeholder={s.form.country === 'IN' ? '98765 43210' : '98 1234 5678'}
                  onChange={(event) => s.setNumber(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') s.sendCode();
                  }}
                  className={inputClass}
                />
              </div>
              {s.turnstile.enabled && (
                <TurnstileBox
                  boxRef={s.turnstile.ref}
                  label={t('turnstile.label')}
                  error={s.turnstile.error}
                />
              )}
              {s.error && <p className="text-xs text-negative">{s.error}</p>}
              <Button full busy={s.sending} onClick={s.sendCode}>
                {t('signin.sendCode')}
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm font-semibold text-ink">
                {t('signin.codeTitle', { sentTo: s.sentTo })}
              </p>
              {s.smsDelayed && <Notice tone="warning" title={t('signin.smsDelayed')} />}
              <input
                aria-label={t('signin.code')}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={s.code}
                onChange={(event) => s.setCode(event.target.value)}
                className={`${inputClass} text-center text-xl tracking-[0.5em]`}
              />
              {s.error && <p className="text-xs text-negative">{s.error}</p>}
              <Button full busy={s.verifying} onClick={s.verify}>
                {t('signin.verify')}
              </Button>
              {s.turnstile.enabled && s.resendIn === 0 && (
                <TurnstileBox
                  boxRef={s.turnstile.ref}
                  label={t('turnstile.label')}
                  error={s.turnstile.error}
                />
              )}
              <div className="flex items-center justify-between text-xs">
                <button type="button" onClick={s.changeNumber} className="font-semibold text-ink">
                  {t('signin.changeNumber')}
                </button>
                {s.resendIn > 0 ? (
                  <span className="text-ink-3 tabular">
                    {t('signin.resendIn', { seconds: s.resendIn })}
                  </span>
                ) : (
                  <button type="button" onClick={s.resend} className="font-semibold text-brand-ink">
                    {t('signin.resend')}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
        <p className="mt-4 text-center text-caption text-ink-3">{t('signin.terms')}</p>
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => navigate(next, { replace: true })}
            className="text-sm font-semibold text-brand-ink"
          >
            {t('signin.browse')}
          </button>
        </div>
      </div>
    </div>
  );
}
